/* eslint-disable */
import {
  CategoryScale,
  Chart as ChartJS,
  Filler,
  Legend,
  LinearScale,
  LineElement,
  PointElement,
  Title,
  Tooltip,
} from 'chart.js';
import 'chartjs-adapter-luxon';
import ChartStreaming from 'chartjs-plugin-streaming';
import { Line } from 'react-chartjs-2';
import { useTranslation } from 'react-i18next';
import { useCallback, useEffect, useMemo, useRef } from 'react';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Legend, ChartStreaming, Filler, Tooltip);

function formatElapsedMmSs(timestampMs, originMs) {
  if (typeof timestampMs !== 'number' || !Number.isFinite(timestampMs)) {
    return '';
  }
  if (typeof originMs !== 'number' || !Number.isFinite(originMs)) {
    return '';
  }

  const elapsedSec = Math.max(0, Math.round((timestampMs - originMs) / 1000));
  const mins = Math.floor(elapsedSec / 60);
  const secs = elapsedSec % 60;
  return `${mins}:${String(secs).padStart(2, '0')}`;
}

function labelRealtimeTicks(scale) {
  const origin = scale.min;
  if (typeof origin !== 'number' || !Number.isFinite(origin)) {
    return;
  }

  scale.ticks.forEach(tick => {
    if (typeof tick.value === 'number' && Number.isFinite(tick.value)) {
      tick.label = formatElapsedMmSs(tick.value, origin);
    }
  });
}

const POWER_SERIES_COLORS = {
  live: {
    backgroundColor: 'rgba(249, 76, 175, 0.14)',
    borderColor: 'rgba(249, 76, 175, 0.88)',
    pointBackgroundColor: '#f94caf',
    pointBorderColor: '#ff9ed4',
  },
  offline: {
    backgroundColor: 'rgba(158, 152, 168, 0.16)',
    borderColor: 'rgba(168, 162, 176, 0.9)',
    pointBackgroundColor: '#9e98a8',
    pointBorderColor: '#c4becc',
  },
};

function applyPowerSeriesColors(dataset, offline) {
  const colors = offline ? POWER_SERIES_COLORS.offline : POWER_SERIES_COLORS.live;
  dataset.backgroundColor = colors.backgroundColor;
  dataset.borderColor = colors.borderColor;
  dataset.pointBackgroundColor = colors.pointBackgroundColor;
  dataset.pointBorderColor = colors.pointBorderColor;
}

export function Chart({ power, voltage, soc, offline = false, onChartClick }) {
  const { t } = useTranslation();
  const chartRef = useRef(null);
  const powerDataRef = useRef([]);
  const voltageDataRef = useRef([]);
  const powerRef = useRef(power);
  const voltageRef = useRef(voltage);
  const socRef = useRef(soc);

  powerRef.current = power;
  voltageRef.current = voltage;
  socRef.current = soc;

  const maxKw = Math.max(Number(power?.maxKw) || 22, 1);

  const onRefresh = useCallback(chart => {
    if (!chart?.data?.datasets) {
      return;
    }

    const now = Date.now();
    chart.data.datasets[0].data.push({
      x: now,
      y: Number(powerRef.current?.currentKw) || 0,
      soc:
        typeof socRef.current === 'number' && Number.isFinite(socRef.current) && socRef.current > 0
          ? socRef.current
          : null,
    });
    chart.data.datasets[1].data.push({
      x: now,
      y: Number(voltageRef.current) || 0,
    });
    chart.update('quiet');
  }, []);

  const chartData = useMemo(
    () => ({
      datasets: [
        {
          label: 'W',
          ...POWER_SERIES_COLORS.live,
          pointRadius: 3,
          pointHoverRadius: 4,
          fill: true,
          lineTension: 0.35,
          cubicInterpolationMode: 'monotone',
          yAxisID: 'power',
          data: powerDataRef.current,
        },
        {
          label: 'V',
          backgroundColor: 'rgba(94, 184, 255, 0.08)',
          borderColor: 'rgba(94, 184, 255, 0.35)',
          fill: true,
          lineTension: 0,
          cubicInterpolationMode: 'monotone',
          yAxisID: 'voltage',
          data: voltageDataRef.current,
        },
      ],
    }),
    []
  );

  const chartOptions = useMemo(() => {
    const axisTickColor = 'rgba(218, 209, 230, 0.58)';
    const axisGridColor = 'rgba(218, 209, 230, 0.1)';

    return {
      responsive: true,
      maintainAspectRatio: true,
      aspectRatio: 2.1,
      animation: false,
      interaction: {
        intersect: false,
        mode: 'index',
      },
      plugins: {
        tooltip: {
          enabled: false,
        },
        legend: {
          display: false,
          onClick: () => null,
        },
        datalabels: {
          display: false,
        },
      },
      scales: {
        power: {
          type: 'linear',
          display: true,
          position: 'left',
          min: 0,
          max: maxKw,
          grid: {
            color: axisGridColor,
            drawBorder: false,
          },
          ticks: {
            color: axisTickColor,
            maxTicksLimit: 6,
            padding: 6,
          },
          border: {
            display: false,
          },
        },
        voltage: {
          type: 'linear',
          display: false,
          position: 'left',
          min: 200,
          max: 220,
          grid: {
            drawOnChartArea: false,
          },
        },
        x: {
          type: 'realtime',
          distribution: 'linear',
          time: {
            unit: 'second',
          },
          realtime: {
            duration: 20000,
            refresh: 1000,
            delay: 1000,
            onRefresh,
          },
          grid: {
            color: axisGridColor,
            drawBorder: false,
          },
          ticks: {
            color: axisTickColor,
            maxRotation: 0,
            autoSkip: true,
            maxTicksLimit: 5,
            padding: 8,
            font: {
              family: 'ui-monospace, SFMono-Regular, Menlo, monospace',
              size: 11,
            },
          },
          afterBuildTicks: labelRealtimeTicks,
          border: {
            display: false,
          },
        },
      },
    };
  }, [maxKw, onRefresh]);

  useEffect(() => {
    const chart = chartRef.current;
    const dataset = chart?.data?.datasets?.[0];
    if (!dataset) {
      return;
    }
    applyPowerSeriesColors(dataset, offline);
    chart.update('none');
  }, [offline]);

  useEffect(() => {
    return () => {
      if (chartRef.current) {
        chartRef.current.destroy();
        chartRef.current = null;
      }
    };
  }, []);

  const handleChartClick = event => {
    if (typeof onChartClick === 'function') {
      onChartClick(event);
    }
  };

  const handleChartKeyDown = event => {
    if ((event.key === 'Enter' || event.key === ' ') && typeof onChartClick === 'function') {
      event.preventDefault();
      onChartClick(event);
    }
  };

  return (
    <div
      id="chartBox"
      className={`charging-power-chart${onChartClick ? ' charging-power-chart--clickable' : ''}`}
      role={onChartClick ? 'button' : undefined}
      tabIndex={onChartClick ? 0 : undefined}
      aria-label={onChartClick ? t('openPowerHistoryChart') : undefined}
      onClick={handleChartClick}
      onKeyDown={handleChartKeyDown}
    >
      <Line ref={chartRef} options={chartOptions} data={chartData} />
    </div>
  );
}
